import {useCallback, useEffect, useMemo} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';

import {
  areProcedureRunInputsEvaluatedValuesValid,
  isNumConditionReplicatesValid,
  validateConditions,
} from '../design-state/procedureRunStudyValidationUtils';
import {ConditionGroup} from '../design-state/useExperimentDesignState';
import {MethodExecutionLimitState} from '../execution-status/useMethodExecutionLimitState';
import {type RunDesignActions, useProcedureRunContext} from '../overview/StudyOverviewContexts';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';

interface UseRunDesignActionsParams {
  /** Legacy expanded runs have no Mantine header, so no actions are published for them. */
  flowchartIsExpanded: boolean;
  studyType: StudyType;
  editState: StudyDesignTabEditState;
  /** Null while inputs are still loading; the save guard treats that as "can't save yet". */
  context: ExperimentalInputsContextProperties | null;
  conditions: Array<ConditionGroup>;
  initialPersistedConditionGroups: Array<ConditionGroup>;
  initialExperimentalInputs: ExperimentalInputs;
  methodExecutionLimitState: MethodExecutionLimitState;
  drawnEdgeSpecCount: number;
  removedEdgeIdCount: number;
  removedNodeIdCount: number;
  conditionReplicateCount: number;
  isActiveLayerConnectedForSave: boolean;
  lowerMethodString: string;
  isEditDisabled: boolean;
  editDisabledTooltip?: string;
  updateEditState: (nextEditState: StudyDesignTabEditState) => void;
  resetConditionsAndInputs: (
    conditions: Array<ConditionGroup>,
    experimentalInputs: ExperimentalInputs
  ) => void;
  resetEdits: () => void;
  resetNodeRemovals: () => void;
  setIsEditBannerDismissed: (dismissed: boolean) => void;
  setIsUpdateRunDesignModalOpen: (open: boolean) => void;
}

/**
 * Whether the run design Save action should be disabled, combining validation and "no pending edits"
 * checks.
 */
function computeIsRunDesignSaveDisabled({
  flowchartIsExpanded,
  context,
  conditions,
  initialPersistedConditionGroups,
  methodExecutionLimitState,
  drawnEdgeSpecCount,
  removedEdgeIdCount,
  removedNodeIdCount,
  conditionReplicateCount,
  isActiveLayerConnectedForSave,
}: Pick<
  UseRunDesignActionsParams,
  | 'flowchartIsExpanded'
  | 'context'
  | 'conditions'
  | 'initialPersistedConditionGroups'
  | 'methodExecutionLimitState'
  | 'drawnEdgeSpecCount'
  | 'removedEdgeIdCount'
  | 'removedNodeIdCount'
  | 'conditionReplicateCount'
  | 'isActiveLayerConnectedForSave'
>): boolean {
  if (flowchartIsExpanded) {
    return true;
  }
  const hasNoPendingEdits =
    conditions.length === initialPersistedConditionGroups.length &&
    drawnEdgeSpecCount === 0 &&
    removedEdgeIdCount === 0 &&
    removedNodeIdCount === 0;
  return (
    context == null ||
    !areProcedureRunInputsEvaluatedValuesValid(context.evaluatedValues) ||
    !validateConditions(conditions) ||
    !isNumConditionReplicatesValid(methodExecutionLimitState) ||
    conditionReplicateCount === 0 ||
    hasNoPendingEdits ||
    !isActiveLayerConnectedForSave
  );
}

function getRunDesignSaveDisabledTooltip({
  conditionReplicateCount,
  isActiveLayerConnectedForSave,
  lowerMethodString,
  studyType,
}: Pick<
  UseRunDesignActionsParams,
  'conditionReplicateCount' | 'isActiveLayerConnectedForSave' | 'lowerMethodString' | 'studyType'
>): string | undefined {
  if (conditionReplicateCount === 0) {
    const runLabel = getProcedureRunUIStringForStudyType(studyType, {capital: true});
    return `${runLabel} must include at least 1 condition-replicate`;
  }
  if (!isActiveLayerConnectedForSave) {
    return `Every ${lowerMethodString} must be connected to the rest of the flowchart.`;
  }
  return undefined;
}

/**
 * Builds the run design edit state and handlers and publishes them to {@link ProcedureRunContext},
 * the context the overview header reads to render the design save/cancel controls. Returns the same
 * actions for the design tab's own sub-header, or null for expanded legacy runs with no Mantine header.
 */
export function useRunDesignActions(params: UseRunDesignActionsParams): RunDesignActions | null {
  const {
    flowchartIsExpanded,
    editState,
    isEditDisabled,
    editDisabledTooltip,
    updateEditState,
    resetConditionsAndInputs,
    initialPersistedConditionGroups,
    initialExperimentalInputs,
    resetEdits,
    resetNodeRemovals,
    setIsEditBannerDismissed,
    setIsUpdateRunDesignModalOpen,
  } = params;

  const onEdit = useCallback(() => {
    setIsEditBannerDismissed(false);
    updateEditState(StudyDesignTabEditState.EDITING);
  }, [setIsEditBannerDismissed, updateEditState]);

  const onCancel = useCallback(() => {
    updateEditState(StudyDesignTabEditState.SAVED);
    resetConditionsAndInputs(initialPersistedConditionGroups, initialExperimentalInputs);
    resetEdits();
    resetNodeRemovals();
  }, [
    updateEditState,
    resetConditionsAndInputs,
    initialPersistedConditionGroups,
    initialExperimentalInputs,
    resetEdits,
    resetNodeRemovals,
  ]);

  const onSave = useCallback(() => {
    setIsUpdateRunDesignModalOpen(true);
  }, [setIsUpdateRunDesignModalOpen]);

  const isSaveDisabled = computeIsRunDesignSaveDisabled(params);

  const saveDisabledTooltip = getRunDesignSaveDisabledTooltip(params);

  const runDesignActions = useMemo<RunDesignActions | null>(
    () =>
      flowchartIsExpanded
        ? null
        : {
            editState,
            onEdit,
            onCancel,
            onSave,
            isSaveDisabled,
            saveDisabledTooltip,
            isEditDisabled,
            editDisabledTooltip,
          },
    [
      flowchartIsExpanded,
      editState,
      onEdit,
      onCancel,
      onSave,
      isSaveDisabled,
      saveDisabledTooltip,
      isEditDisabled,
      editDisabledTooltip,
    ]
  );

  const {setRunDesignActions} = useProcedureRunContext();
  useEffect(() => {
    setRunDesignActions(runDesignActions);
    return () => {
      setRunDesignActions(null);
    };
  }, [setRunDesignActions, runDesignActions]);

  return runDesignActions;
}
